import React, { useEffect, useState } from "react";
import {
  Coins, IndianRupee, Send, Wallet, Download, Loader2, PencilLine,
  ArrowDownToLine, ArrowUpFromLine, ClipboardList, ShieldAlert, PlusCircle,
} from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

const grams = (g) => `${(Number(g) || 0).toFixed(3)} g`;
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtWhen = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

// One Inward/Outward category's breakdown — a small table of the underlying
// transaction sources that roll up into that category's total (e.g. Gold
// Inward = Deposits + Buy-backs, each with its own source lines beneath).
function BreakdownTable({ label, data, unit, testid }) {
  const fmt = unit === "gold" ? grams : formatINR;
  const lines = data?.lines || [];
  return (
    <div className="mb-4 last:mb-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium">{label}</span>
        <span className={`font-mono text-sm font-semibold ${unit === "gold" ? "text-[hsl(var(--gold-dark))]" : "text-foreground"}`}>{fmt(data?.total_g ?? data?.total ?? 0)}</span>
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableBody>
            {lines.map((l, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs py-2">{l.label}</TableCell>
                <TableCell className="text-xs py-2 text-muted-foreground text-right w-16">{l.count ? `×${l.count}` : "—"}</TableCell>
                <TableCell className="text-xs py-2 text-right font-mono w-28">{fmt(l.grams ?? l.amount ?? 0)}</TableCell>
              </TableRow>
            ))}
            {lines.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-xs py-3 text-center text-muted-foreground">No activity</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function DailyDaybook() {
  const [date, setDate] = useState(todayStr());
  const [startConfig, setStartConfig] = useState(undefined); // undefined = loading, null = not set
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [staff, setStaff] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [setupForm, setSetupForm] = useState({ date: todayStr(), opening_gold_g: "", opening_inr: "" });
  const [settingUp, setSettingUp] = useState(false);

  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionForm, setCorrectionForm] = useState({ opening_gold_g: "", opening_inr: "", reason: "" });
  const [savingCorrection, setSavingCorrection] = useState(false);

  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category: "General", amount: "", paid_to: "", note: "" });
  const [savingExpense, setSavingExpense] = useState(false);

  const loadStart = async () => {
    try {
      const { data } = await api.get("/daybook/start");
      setStartConfig(data?.date ? data : null);
    } catch {
      setStartConfig(null);
    }
  };

  const loadReport = async (d) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/daybook/report", { params: { date: d } });
      setReport(data);
    } catch (e) {
      setReport(null);
      setError(e.response?.data?.detail || "Failed to load the daybook report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStart();
    api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (startConfig) loadReport(date);
    else if (startConfig === null) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startConfig, date]);

  const initStart = async () => {
    if (!setupForm.date) { toast.error("Pick a start date"); return; }
    setSettingUp(true);
    try {
      const { data } = await api.put("/daybook/start", {
        date: setupForm.date,
        opening_gold_g: Number(setupForm.opening_gold_g) || 0,
        opening_inr: Number(setupForm.opening_inr) || 0,
      });
      setStartConfig(data.start);
      setDate(setupForm.date);
      toast.success("Daybook initialized");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initialize the Daybook");
    } finally { setSettingUp(false); }
  };

  const openCorrection = () => {
    setCorrectionForm({
      opening_gold_g: String(report?.opening_gold_g ?? 0),
      opening_inr: String(report?.opening_inr ?? 0),
      reason: "",
    });
    setCorrectionOpen(true);
  };

  const saveCorrection = async () => {
    if (!correctionForm.reason.trim()) { toast.error("A reason is required for every manual correction"); return; }
    setSavingCorrection(true);
    try {
      const { data } = await api.put("/daybook/opening", {
        date, opening_gold_g: Number(correctionForm.opening_gold_g) || 0,
        opening_inr: Number(correctionForm.opening_inr) || 0, reason: correctionForm.reason.trim(),
      });
      setReport(data.report);
      toast.success("Opening balance corrected");
      setCorrectionOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save correction");
    } finally { setSavingCorrection(false); }
  };

  const openExpense = () => { setExpenseForm({ category: "General", amount: "", paid_to: "", note: "" }); setExpenseOpen(true); };
  const saveExpense = async () => {
    const amt = Number(expenseForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid expense amount"); return; }
    setSavingExpense(true);
    try {
      await api.post("/daybook/expenses", { date, ...expenseForm, amount: amt });
      toast.success("Expense logged");
      setExpenseOpen(false);
      loadReport(date);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log expense");
    } finally { setSavingExpense(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/daybook/report/pdf", { params: { date }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Daybook-${date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download the Daybook PDF");
    } finally { setDownloading(false); }
  };

  const isManager = staff?.is_manager;

  return (
    <AdminShell title="Daily Daybook">
      {/* Date + Download bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Label htmlFor="daybook-date" className="text-xs text-muted-foreground">Report Date</Label>
          <Input id="daybook-date" data-testid="daybook-date-input" type="date" value={date}
                 max={todayStr()} onChange={(e) => setDate(e.target.value)} className="w-44" />
        </div>
        <Button data-testid="daybook-download-button" className="gap-2" disabled={!report || downloading} onClick={downloadPdf}>
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download Daybook
        </Button>
      </div>

      {/* Setup state: no Daybook Start configured yet */}
      {startConfig === undefined ? (
        <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>
      ) : startConfig === null ? (
        <Card className="p-6 max-w-lg" data-testid="daybook-setup-card">
          <div className="flex items-center gap-2 mb-1 text-[hsl(var(--primary))]"><ShieldAlert className="h-5 w-5" /><span className="font-display text-lg">Set Up the Daybook</span></div>
          <p className="text-sm text-muted-foreground mb-4">Pick the date you want the Daybook to start tracking from, and enter the opening Gold and Rupee balances on that date. Every later date rolls forward automatically from here.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input data-testid="daybook-setup-date" type="date" max={todayStr()} value={setupForm.date}
                     onChange={(e) => setSetupForm({ ...setupForm, date: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Opening Gold (fine g)</Label>
                <Input data-testid="daybook-setup-gold" type="number" step="0.001" value={setupForm.opening_gold_g}
                       onChange={(e) => setSetupForm({ ...setupForm, opening_gold_g: e.target.value })} className="mt-1 font-mono" placeholder="0.000" />
              </div>
              <div>
                <Label className="text-xs">Opening Rupee (₹)</Label>
                <Input data-testid="daybook-setup-inr" type="number" value={setupForm.opening_inr}
                       onChange={(e) => setSetupForm({ ...setupForm, opening_inr: e.target.value })} className="mt-1 font-mono" placeholder="0" />
              </div>
            </div>
            <Button data-testid="daybook-setup-submit" className="w-full" disabled={settingUp} onClick={initStart}>
              {settingUp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Initialize Daybook
            </Button>
          </div>
        </Card>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : error ? (
        <Card className="p-8 text-center" data-testid="daybook-error-state">
          <p className="text-sm text-muted-foreground mb-3">{error}</p>
          <Button variant="outline" size="sm" onClick={() => loadReport(date)}>Retry</Button>
        </Card>
      ) : report ? (
        <>
          {/* 4 category totals */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="p-4" data-testid="daybook-kpi-gold-in">
              <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Gold Received</span><ArrowDownToLine className="h-4 w-4 text-[hsl(var(--gold-dark))]" /></div>
              <div className="font-mono text-2xl mt-2 text-[hsl(var(--gold-dark))]">{grams(report.gold_inward.total_g)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Deposits + Buy-backs</div>
            </Card>
            <Card className="p-4" data-testid="daybook-kpi-cash-in">
              <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Cash Received</span><ArrowDownToLine className="h-4 w-4 text-[#1F5B2E]" /></div>
              <div className="font-mono text-2xl mt-2 text-[#1F5B2E]">{formatINR(report.cash_inward.total)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Rupee Payments</div>
            </Card>
            <Card className="p-4" data-testid="daybook-kpi-gold-out">
              <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Gold Issued</span><ArrowUpFromLine className="h-4 w-4 text-[#7A1F1F]" /></div>
              <div className="font-mono text-2xl mt-2 text-[#7A1F1F]">{grams(report.gold_outward.total_g)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Sales + Karigar Issues</div>
            </Card>
            <Card className="p-4" data-testid="daybook-kpi-cash-out">
              <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Cash Paid</span><ArrowUpFromLine className="h-4 w-4 text-[#7A1F1F]" /></div>
              <div className="font-mono text-2xl mt-2 text-[#7A1F1F]">{formatINR(report.cash_outward.total)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Expenses + Supplier Payments</div>
            </Card>
          </div>

          {/* Closing Position */}
          <Card className="p-5 mb-6" data-testid="daybook-closing-position">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="font-display text-lg">Closing Position</h2>
              {isManager && (
                <Button data-testid="daybook-correct-opening-button" size="sm" variant="outline" className="gap-1.5" onClick={openCorrection}>
                  <PencilLine className="h-3.5 w-3.5" /> Correct Opening Balance
                </Button>
              )}
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow><TableHead></TableHead><TableHead className="text-right">Gold (fine 24K)</TableHead><TableHead className="text-right">Rupee</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-sm">Opening Balance</TableCell>
                    <TableCell data-testid="daybook-opening-gold" className="text-right font-mono">{grams(report.opening_gold_g)}</TableCell>
                    <TableCell data-testid="daybook-opening-inr" className="text-right font-mono">{formatINR(report.opening_inr)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-sm text-[#1F5B2E]">(+) Total Inward</TableCell>
                    <TableCell className="text-right font-mono text-[#1F5B2E]">{grams(report.gold_inward.total_g)}</TableCell>
                    <TableCell className="text-right font-mono text-[#1F5B2E]">{formatINR(report.cash_inward.total)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-sm text-[#7A1F1F]">(−) Total Outward</TableCell>
                    <TableCell className="text-right font-mono text-[#7A1F1F]">{grams(report.gold_outward.total_g)}</TableCell>
                    <TableCell className="text-right font-mono text-[#7A1F1F]">{formatINR(report.cash_outward.total)}</TableCell>
                  </TableRow>
                  <TableRow className="bg-[hsl(var(--gold-light))]">
                    <TableCell className="text-sm font-semibold">Closing Balance</TableCell>
                    <TableCell data-testid="daybook-closing-gold" className="text-right font-mono font-semibold">{grams(report.closing_gold_g)}</TableCell>
                    <TableCell data-testid="daybook-closing-inr" className="text-right font-mono font-semibold">{formatINR(report.closing_inr)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Opening rolled forward from {report.opening_anchor_source === "correction" ? "the last manual correction" : "the Daybook Start"} dated {report.opening_anchor_date}.</p>
          </Card>

          {/* Inward / Outward Flow */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card className="p-5" data-testid="daybook-inward-flow">
              <div className="flex items-center gap-2 mb-4"><ArrowDownToLine className="h-4 w-4 text-[hsl(var(--gold-dark))]" /><h2 className="font-display text-lg">Inward Flow</h2></div>
              <BreakdownTable label="Deposits (Karigar Receipts)" data={report.gold_inward.deposits} unit="gold" testid="daybook-deposits" />
              <BreakdownTable label="Buy-backs (Old Gold / Scrap)" data={report.gold_inward.buybacks} unit="gold" testid="daybook-buybacks" />
              <BreakdownTable label="Rupee Payments" data={report.cash_inward.rupee_payments} unit="inr" testid="daybook-rupee-payments" />
            </Card>
            <Card className="p-5" data-testid="daybook-outward-flow">
              <div className="flex items-center gap-2 mb-4"><ArrowUpFromLine className="h-4 w-4 text-[#7A1F1F]" /><h2 className="font-display text-lg">Outward Flow</h2></div>
              <BreakdownTable label="Sales" data={report.gold_outward.sales} unit="gold" testid="daybook-sales" />
              <BreakdownTable label="Karigar Issues" data={report.gold_outward.karigar_issues} unit="gold" testid="daybook-karigar-issues" />
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium">Expenses</span>
                {isManager && (
                  <Button data-testid="daybook-add-expense-button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={openExpense}>
                    <PlusCircle className="h-3.5 w-3.5" /> Add Expense
                  </Button>
                )}
              </div>
              <div className="rounded-lg border border-border overflow-hidden mb-4">
                <Table>
                  <TableBody>
                    {(report.cash_outward.expenses.lines || []).map((l, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs py-2">{l.label}</TableCell>
                        <TableCell className="text-xs py-2 text-right font-mono w-28">{formatINR(l.amount)}</TableCell>
                      </TableRow>
                    ))}
                    {(report.cash_outward.expenses.lines || []).length === 0 && (
                      <TableRow><TableCell colSpan={2} className="text-xs py-3 text-center text-muted-foreground">No expenses logged for this date</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <BreakdownTable label="Supplier Payments" data={report.cash_outward.supplier_payments} unit="inr" testid="daybook-supplier-payments" />
            </Card>
          </div>

          {/* Manual corrections audit log */}
          {report.corrections?.length > 0 && (
            <Card className="p-5" data-testid="daybook-corrections-log">
              <div className="flex items-center gap-2 mb-3"><ClipboardList className="h-4 w-4 text-muted-foreground" /><h2 className="font-display text-lg">Opening Balance Corrections (Audit Log)</h2></div>
              <div className="rounded-lg border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>When</TableHead><TableHead>By</TableHead><TableHead className="text-right">Opening Gold</TableHead><TableHead className="text-right">Opening Rupee</TableHead><TableHead>Reason</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.corrections.map((c) => (
                      <TableRow key={c.id} data-testid="daybook-correction-row">
                        <TableCell className="text-xs whitespace-nowrap">{fmtWhen(c.at)}</TableCell>
                        <TableCell className="text-xs">{c.by_name}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{grams(c.opening_gold_g)}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{formatINR(c.opening_inr)}</TableCell>
                        <TableCell className="text-xs">{c.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </>
      ) : null}

      {/* Correct Opening Balance dialog */}
      <Dialog open={correctionOpen} onOpenChange={setCorrectionOpen}>
        <DialogContent data-testid="daybook-correction-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Correct Opening Balance — {date}</DialogTitle>
            <DialogDescription>Overrides the auto-rolled opening balance for this date onward. Every correction is logged with a reason for audit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Opening Gold (fine g)</Label>
              <Input data-testid="daybook-correction-gold" type="number" step="0.001" value={correctionForm.opening_gold_g}
                     onChange={(e) => setCorrectionForm({ ...correctionForm, opening_gold_g: e.target.value })} className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Opening Rupee (₹)</Label>
              <Input data-testid="daybook-correction-inr" type="number" value={correctionForm.opening_inr}
                     onChange={(e) => setCorrectionForm({ ...correctionForm, opening_inr: e.target.value })} className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Reason (required)</Label>
              <Textarea data-testid="daybook-correction-reason" value={correctionForm.reason}
                        onChange={(e) => setCorrectionForm({ ...correctionForm, reason: e.target.value })} rows={2} className="mt-1" placeholder="e.g. Physical stock count adjustment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionOpen(false)}>Cancel</Button>
            <Button data-testid="daybook-correction-save" disabled={savingCorrection || !correctionForm.reason.trim()} onClick={saveCorrection}>
              {savingCorrection ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Expense dialog */}
      <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
        <DialogContent data-testid="daybook-expense-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Log Expense — {date}</DialogTitle>
            <DialogDescription>Counted under Cash Outward for this date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Input data-testid="daybook-expense-category" value={expenseForm.category}
                     onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })} className="mt-1" placeholder="e.g. Office, Travel, Utilities" />
            </div>
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input data-testid="daybook-expense-amount" type="number" value={expenseForm.amount}
                     onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} className="mt-1 font-mono" placeholder="0" />
            </div>
            <div>
              <Label className="text-xs">Paid To</Label>
              <Input data-testid="daybook-expense-paid-to" value={expenseForm.paid_to}
                     onChange={(e) => setExpenseForm({ ...expenseForm, paid_to: e.target.value })} className="mt-1" placeholder="Optional" />
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea data-testid="daybook-expense-note" value={expenseForm.note}
                        onChange={(e) => setExpenseForm({ ...expenseForm, note: e.target.value })} rows={2} className="mt-1" placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseOpen(false)}>Cancel</Button>
            <Button data-testid="daybook-expense-save" disabled={savingExpense || !expenseForm.amount} onClick={saveExpense}>
              {savingExpense ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Log Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
